import React, { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import emailjs from '@emailjs/browser';
import { 
  FiMapPin, 
  FiPhone, 
  FiMail, 
  FiClock,
  FiSend
} from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import PhilippinesMap from '../components/PhilippinesMap';
import Chatbot from '../components/Chatbot';
import '../styles/pages/Contact.css';
import '../styles/pages/About.css';
import '../styles/animations/ScrollAnimations.css';

// Fix for default marker icon in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom green marker
const greenIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const Contact = () => {
  const formRef = useRef();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('[class*="scroll-"]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // MEDCoop exact coordinates
  const position = [7.068536, 125.611843];

  const areasOfOperation = {
    headOffice: {
      name: 'HEAD OFFICE',
      address: '3rd Floor Marcela Building, Palma Gil Street, Barangay 34-D, Davao City',
      phone: ['(082) 287-7886'],
      hrNumbers: ['0998-846-3886', '0918-912-0332'],
      operationManager: '0919-067-9028',
      seniorManager: '0998-593-0950',
      customerService: '0998-593-0902'
    },
    branches: [
      {
        name: 'LUZON BRANCH',
        manager: { name: 'Frosie Adecer', position: 'Luzon General Manager' },
        address: '68 EDSA cor Pinatubo St. Barangay Malamig, Mandaluyong City',
        staff: [
          { name: 'Arlyn Abellera', position: 'Luzon Office Admin' },
          { name: 'Charmane Fernandez', position: 'Luzon Office Admin-Billing Staff', contact: '0920-940-0781' },
          { name: 'Mark Jayson Retardo', position: 'Luzon Office Admin-Billing Staff', contact: '0920-940-0781' }
        ],
        coordinators: [
          { name: 'Francisco, Walter', position: 'Supervisor', contact: '0927-294-3707', areas: ['NCR Area'] },
          { name: 'Raizen Rivera', position: 'NCR Coordinator', contact: '0998-845-1139', areas: ['Makati'] },
          { name: 'Jeffrey Delumen', position: 'NCR Coordinator', contact: '0918-929-2673', areas: ['Quezon City', 'San Juan City'] },
          { name: 'Gershon Fin', position: 'NCR Coordinator', contact: '0920-907-9561', areas: ['Mandaluyong', 'Marikina', 'Pasig', 'Makati', 'Pateros', 'Taguig'] },
          { name: 'Brian Carl Y Asahan', position: 'NCR Coordinator', contact: '0918-919-7531', areas: ['Las Piñas', 'Parañaque', 'Pasay', 'Muntinlupa'] },
          { name: 'Paul Andrew Mallari', position: 'NCR Coordinator', contact: '0976-025-7472', areas: ['Manila', 'Camanava', 'Quezon City District 5'] },
          { name: 'Julius Unson', position: 'North Luzon Coordinator', contact: '0920-978-1611', areas: ['Region 1', 'Cordillera Administrative Region', 'Region 2', 'Region 3'] },
          { name: 'Christopher Bañas', position: 'South Luzon Coordinator', contact: '0999-226-2397', areas: ['Region 4A (Batangas, Carmona, Laguna, Quezon, Rizal)', 'Region 4B', 'Region 5'] },
          { name: 'Maryjoy D. Dela Cruz', position: 'South Luzon Coordinator', contact: '0998-553-7253', areas: ['Cavite', 'Las Piñas', 'Mandaluyong', 'Taguig'] },
          { name: 'Rodel Patente', position: 'South Luzon Coordinator', contact: '0919-076-8026', areas: ['Region 4A (Cavite, Batangas)'] }
        ]
      },
      {
        name: 'VISAYAS - BACOLOD BRANCH',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        address: 'Mackinly Baybay St. cor. Sto. Niño Brgy. Mambulac, Silay City, Negros Occidental',
        managerContact: '0919-077-8394',
        staff: [],
        coordinators: [
          { name: 'Marlon A. Sueta', position: 'Coordinator', contact: '0927-294-3707', areas: ['Bacolod Area'] },
          { name: 'Joseph Malayan', position: 'Regional Area Head', contact: '0949-993-0301', areas: ['Negros Occidental'] }
        ]
      },
      {
        name: 'VISAYAS - CEBU BRANCH',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        address: '6th St, Sitio Kana-as, Guadalajara, Guadalupe Cebu City',
        managerContact: '0919-077-8394',
        staff: [
          { name: 'Ge-R Gemina', position: 'Admin Staff', contact: '0926-007-1549' }
        ],
        coordinators: [
          { name: 'Kenneth Gordon', position: 'Coordinator', contact: '0936-814-9946', areas: ['Cebu City'] },
          { name: 'Marc Nicole Z. Gemina', position: 'Coordinator', contact: '0968-851-3.739', areas: ['Cebu Area'] },
          { name: 'Joseph Malayan', position: 'Regional Area Head', contact: '0949-993-0301', areas: ['Cebu Region'] }
        ]
      },
      {
        name: 'VISAYAS - DUMAGUETE BRANCH',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        address: 'South Bacacay, Dumaguete City',
        managerContact: '0919-077-8394',
        staff: [],
        coordinators: [
          { name: 'Rene Arajji', position: 'Coordinator', contact: '0908-891-9353', areas: ['Dumaguete City'] },
          { name: 'Joseph Malayan', position: 'Regional Area Head', contact: '0949-993-0301', areas: ['Negros Oriental'] }
        ]
      },
      {
        name: 'VISAYAS - ILOILO BRANCH',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        address: '206 MH Del Pilar St. Molo, Iloilo City',
        managerContact: '0919-077-8394',
        staff: [],
        coordinators: [
          { name: 'R-Jee Herrera', position: 'Coordinator', contact: '0998-891-9353', areas: ['Iloilo City'] },
          { name: 'Joseph Malayan', position: 'Regional Area Head', contact: '0949-993-0301', areas: ['Iloilo Region'] }
        ]
      },
      {
        name: 'VISAYAS - BOHOL BRANCH',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        address: 'Felis Garbiela Bldg. Castillo - Facifico Road Barangay Dao, Tagbilaran Bohol',
        managerContact: '0919-077-8394',
        staff: [],
        coordinators: [
          { name: 'Rufino Urasaga', position: 'Coordinator', contact: '0912-833-9144', areas: ['Bohol'] },
          { name: 'Joseph Malayan', position: 'Regional Area Head', contact: '0949-993-0301', areas: ['Bohol Region'] }
        ]
      },
      {
        name: 'VISAYAS - LEYTE/TACLOBAN BRANCH',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        address: 'Palo, Alang-Alang Marasbaras, Tacloban City',
        managerContact: '0919-067-9028',
        staff: [],
        coordinators: [
          { name: 'Lanze Audrie Roice T. Daquiado', position: 'Coordinator', contact: '0946-455-6478', areas: ['Leyte', 'Tacloban'] }
        ]
      },
      {
        name: 'VISAYAS - SOUTHERN LEYTE BRANCH',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        address: 'P. Gonzales St. Abgao, Maasin City',
        managerContact: '0919-067-9028',
        staff: [],
        coordinators: [
          { name: 'Renan Alizada', position: 'Coordinator', contact: '0999-225-1439', areas: ['Southern Leyte', 'Maasin City'] }
        ]
      },
      {
        name: 'VISAYAS - ORMOC LEYTE BRANCH',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        address: 'District 21 San Vedal and Real St. Ormoc City',
        managerContact: '0919-067-9028',
        staff: [],
        coordinators: [
          { name: 'Dennis Albarando', position: 'Coordinator', contact: '0951-045-2701', areas: ['Ormoc City'] }
        ]
      },
      {
        name: 'VISAYAS - SAMAR LEYTE BRANCH',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        address: 'Eastwest Bank Brgy. 5 Curry Avenue, Corner San Bartolome St. Catbalogan City',
        managerContact: '0919-067-9028',
        staff: [],
        coordinators: [
          { name: 'Daniel Diocton', position: 'Coordinator', contact: '0985-854-3046', areas: ['Samar', 'Catbalogan City'] }
        ]
      },
      {
        name: 'MINDANAO - HEAD OFFICE DAVAO',
        manager: { name: 'Myra Betonio', position: 'Senior Operation Manager' },
        address: '3rd Floor Marcela Building, Palma Gil Street, Barangay 34-D, Davao City',
        managerContact: '0998-593-0950',
        phone: ['(082) 287-7886'],
        hrNumbers: ['0998-846-3886', '0918-912-0332'],
        operationManager: '0919-067-9028',
        customerService: '0998-593-0902',
        staff: [
          { name: 'Dave Cueme', position: 'Branch Operation Manager of Region 11, 12, 8', contact: '0919-067-9028' },
          { name: 'Rudyrick John Perez', position: 'Branch Operation Manager of Region 6, 7, 9', contact: '0919-077-8394' }
        ],
        coordinators: [
          { name: 'Nickole Christian Endraca', position: 'South Cluster Head', contact: '0999-229-5941', areas: ['South Davao Cluster'] },
          { name: 'Noel Futalan', position: 'North Cluster Head', contact: '0998-593-0904', areas: ['North Davao Cluster'] },
          { name: 'Jonnifer Vinson', position: 'Cluster Head', contact: '0916-910-3029', areas: ['Davao Cluster'] }
        ]
      },
      {
        name: 'MINDANAO - BARM BRANCH',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        address: 'Door 4, Enilo Apartment RH-4 Sinsuat',
        managerContact: '0919-067-9028',
        staff: [],
        coordinators: [
          { name: 'Ryan Robles', position: 'Coordinator', contact: '0998-590-7841', areas: ['BARM Region'] }
        ]
      },
      {
        name: 'MINDANAO - BUTUAN BRANCH',
        manager: { name: 'Reymart Ligue', position: 'Regional Area Head' },
        address: 'Dr. 3 Sevilla Apt. Nonan Village Barangay Bayanihan JC Aquino Avenue, Butuan City',
        managerContact: '0968-867-2181',
        staff: [
          { name: 'Alma Bada', position: 'Admin Staff', contact: '0998-593-0903' }
        ],
        coordinators: [
          { name: 'Allan Lobo', position: 'Coordinator', contact: '0998-590-7833', areas: ['Butuan City'] }
        ]
      },
      {
        name: 'MINDANAO - CAGAYAN DE ORO BRANCH',
        manager: { name: 'Reymart Ligue', position: 'Regional Area Head' },
        address: 'Bontong, Camaman-an Cagayan de Oro',
        managerContact: '0968-867-2181',
        staff: [
          { name: 'Flordeliza Bacongius', position: 'Admin Staff', contact: '0908-864-1056' }
        ],
        coordinators: [
          { name: 'Mark Anthony M. Carreon', position: 'Coordinator', contact: '0975-824-3825', areas: ['Cagayan de Oro'] }
        ]
      },
      {
        name: 'MINDANAO - DIPOLOG BRANCH',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        address: 'Dipolog City',
        managerContact: '0919-077-8394',
        staff: [],
        coordinators: [
          { name: 'Sally Bantilan', position: 'Coordinator', contact: '0965-785-1898', areas: ['Dipolog City'] }
        ]
      },
      {
        name: 'MINDANAO - GENSAN BRANCH',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        address: 'No. 55 Manga Street, Barangay Dadiangas North, General Santos City',
        managerContact: '0919-067-9028',
        staff: [
          { name: 'Lezlie Ocampo', position: 'Admin Staff', contact: '0910-656-0709' }
        ],
        coordinators: [
          { name: 'Gerald Lamata', position: 'Coordinator', contact: '0927-479-6364', areas: ['General Santos City'] }
        ]
      },
      {
        name: 'MINDANAO - MALAYBALAY BRANCH',
        manager: { name: 'Reymart Ligue', position: 'Regional Area Head' },
        address: 'Malaybalay Water District/Primewater Sayre Highway, Malaybalay, Bukidnon',
        managerContact: '0968-867-2181',
        staff: [],
        coordinators: [
          { name: 'Raniel Cameros', position: 'Coordinator', contact: '0998-590-7830', areas: ['Malaybalay', 'Bukidnon'] }
        ]
      },
      {
        name: 'MINDANAO - MISAMIS BRANCH',
        manager: { name: 'Reymart Ligue', position: 'Regional Area Head' },
        address: 'Bypass Road, Villanueva Misamis Oriental',
        managerContact: '0968-867-2181',
        staff: [
          { name: 'Ombra, April Rose/Mausisa Cleo', position: 'Admin Staff', contact: '' }
        ],
        coordinators: [
          { name: 'Nicholas Rafael Morga', position: 'Coordinator', contact: '0998-991-2495', areas: ['Misamis Oriental'] },
          { name: 'Mila Aucila', position: 'Coordinator', contact: '0998-585-9108', areas: ['Misamis'] }
        ]
      },
      {
        name: 'MINDANAO - OZAMIZ BRANCH',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        address: 'Purok2, Badong Exec. Village Barangay Carangan, Ozamiz City',
        managerContact: '0919-077-8394',
        staff: [],
        coordinators: [
          { name: 'Samson L. Gravino', position: 'Coordinator', contact: '0919-067-9025', areas: ['Ozamiz City'] }
        ]
      },
      {
        name: 'MINDANAO - PAGADIAN BRANCH',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        address: 'Purok Kamansi, Lenienza Pagadian City',
        managerContact: '0919-077-8394',
        staff: [
          { name: 'Jan Roe N. Antimano', position: 'Admin Staff', contact: '0951-533-7200' }
        ],
        coordinators: [
          { name: 'Neil Genciane', position: 'Coordinator', contact: '0968-850-2837', areas: ['Pagadian City'] },
          { name: 'Toto Genciane', position: 'Operation Manager', contact: '', areas: ['Pagadian Region'] }
        ]
      },
      {
        name: 'MINDANAO - SURIGAO BRANCH',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        address: 'San Juan Lipata Road 7, Barangay Sabang, Surigao City',
        managerContact: '0919-067-9028',
        staff: [],
        coordinators: [
          { name: 'Christian Olalo', position: 'Coordinator', contact: '0998-586-6867', areas: ['Surigao City'] }
        ]
      },
      {
        name: 'MINDANAO - VALENCIA BRANCH',
        manager: { name: 'Reymart Ligue', position: 'Regional Area Head' },
        address: 'Purok-13-A Pobalcion Hagkol, Valencia City Bukidnon',
        managerContact: '0968-867-2181',
        staff: [],
        coordinators: [
          { name: 'Nestor Villanueva', position: 'Coordinator', contact: '0949-993-0306', areas: ['Valencia City', 'Bukidnon'] }
        ]
      },
      {
        name: 'MINDANAO - ZAMBOANGA BRANCH',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        address: 'District 21 San Vedal and Real St. Ormoc City',
        managerContact: '0919-077-8394',
        staff: [],
        coordinators: [
          { name: 'Cris Umpad', position: 'Coordinator', contact: '0951-045-2701', areas: ['Zamboanga City'] },
          { name: 'Gian Gellor', position: 'Coordinator', contact: '', areas: ['Zamboanga Region'] }
        ]
      }
    ]
  };

  const contactInfo = [
    {
      icon: <FiMapPin />,
      title: 'Visit Us',
      content: 'Marcela Building, 11 J. Palma Gil Street, Poblacion District, Davao City, 8000'
    },
    {
      icon: <FiPhone />,
      title: 'Call Us',
      content: '(082) 287-7886 | HR: 0998-846-3886 / 0918-912-0332 | Customer Service: 0998-593-0902',
      link: 'tel:+63822877886'
    },
    {
      icon: <FiMail />,
      title: 'Email Us',
      content: 'medcoopmsia@gmail.com',
      link: 'mailto:medcoopmsia@gmail.com'
    },
    {
      icon: <FiClock />,
      title: 'Office Hours',
      content: 'Mon - Fri: 8:00 AM - 5:00 PM'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'phone' ? value.replace(/\D/g, '').slice(0, 11) : value;

    setFormData({
      ...formData,
      [name]: nextValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Get current date and time
    const now = new Date();
    const timeString = now.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Prepare template parameters for EmailJS (matching your template variables)
    const templateParams = {
      name: `${formData.firstName} ${formData.lastName}`,
      time: timeString,
      message: `Email: ${formData.email}\nPhone: ${formData.phone || 'Not provided'}\nSubject: ${formData.subject}\n\n${formData.message}`
    };

    try {
      // Send email using EmailJS
      await emailjs.send(
        'service_o1qvz9e', // EmailJS service ID
        'template_jjboiza', // EmailJS template ID
        templateParams,
        'T--16GqMqnCPUL1m-' // EmailJS public key
      );
      
      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Email send error:', error);
      // Fallback: Open email client with pre-filled information
      const mailtoLink = `mailto:medcoopmsia@gmail.com?subject=${encodeURIComponent(formData.subject + ' - ' + formData.firstName + ' ' + formData.lastName)}&body=${encodeURIComponent(
        `Name: ${formData.firstName} ${formData.lastName}\n` +
        `Email: ${formData.email}\n` +
        `Phone: ${formData.phone || 'Not provided'}\n` +
        `Subject: ${formData.subject}\n\n` +
        `Message:\n${formData.message}`
      )}`;
      window.location.href = mailtoLink;
      setSubmitStatus('fallback');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1>{t('contact.title')}</h1>
          <p>
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="contact-info">
        <div className="container">
          <div className="info-cards scroll-scale">
            {contactInfo.map((info, index) => (
              <div className="info-card" key={index}>
                <div className="info-icon">{info.icon}</div>
                <h4>{info.title}</h4>
                {info.link ? (
                  <p><a href={info.link}>{info.content}</a></p>
                ) : (
                  <p>{info.content}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="contact-main section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-form-wrapper scroll-fade-left">
              <h3>Send Us a Message</h3>
              <p>Fill out the form below and we'll get back to you as soon as possible.</p>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input 
                      type="text" 
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input 
                      type="text" 
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      inputMode="numeric"
                      maxLength={11}
                      pattern="[0-9]{11}"
                      title="Phone number must be exactly 11 digits"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Subject *</label>
                  <select 
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a subject</option>
                    <option value="manpower">Manpower Request</option>
                    <option value="quote">Request a Quote</option>
                    <option value="careers">Job Application</option>
                    <option value="support">General Support</option>
                    <option value="feedback">Feedback/Suggestions</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Message *</label>
                  <textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="5"
                    placeholder="How can we help you?"
                    required
                  ></textarea>
                </div>
                {submitStatus === 'success' && (
                  <div className="form-success-message">
                    ✓ Thank you for your message! We will get back to you soon.
                  </div>
                )}
                {submitStatus === 'fallback' && (
                  <div className="form-info-message">
                    Your email client has been opened. Please send the email to complete your inquiry.
                  </div>
                )}
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'} <FiSend />
                </button>
              </form>
            </div>
            <div className="contact-map">
              <div className="map-container">
                <MapContainer 
                  center={position} 
                  zoom={17} 
                  scrollWheelZoom={true}
                  style={{ height: '100%', width: '100%', borderRadius: '16px' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={position} icon={greenIcon}>
                    <Popup>
                      <strong>MEDCoop</strong><br />
                      Marcela Building, 11 J. Palma Gil Street<br />
                      Poblacion District, Davao City, 8000<br />
                      <a href="tel:+63822851120">(082) 285 1120</a>
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
              <div className="map-overlay-info">
                <FiMapPin className="map-pin-icon" />
                <div>
                  <h4>MEDCoop</h4>
                  <p>Marcela Building, 11 J. Palma Gil Street</p>
                  <p>Poblacion District, Davao City, 8000</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas of Operation Section - Interactive Map */}
      <section className="areas-section section">
        <div className="container">
          <div className="section-header scroll-fade-up">
            <h2 className="section-title">Areas of Operation</h2>
            <p className="section-subtitle">
              Serving clients nationwide across the Philippines - Click on any pin to view branch details
            </p>
          </div>

          <div className="scroll-fade-up">
            <PhilippinesMap areasOfOperation={areasOfOperation} />
          </div>
        </div>
      </section>

      {/* Chatbot */}
      <Chatbot />
    </div>
  );
};

export default Contact;

